import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { apiRequest } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import { BookingStatus } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { PaymentBreakdown } from "@/components/payments/PaymentBreakdown";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import type { StripeError } from "@stripe/stripe-js";
import { getStripe } from "@/lib/stripe";
import { Sidebar } from "@/components/layout/Sidebar";
import { User, Home, Calendar, LogOut } from "lucide-react";

// Initialize Stripe
const stripePromise = getStripe();

// Form schema - make fields optional since they may be hidden for logged-in users
const bookingFormSchema = z.object({
  guestName: z.string().optional(),
  guestEmail: z.string().email("Valid email is required").optional(),
  guestPhone: z.string().optional(),
  specialRequests: z.string().optional(),
});

type BookingFormValues = z.infer<typeof bookingFormSchema>;

interface BookingFormProps {
  propertyId: number;
  checkInDate: Date;
  checkOutDate: Date;
  guestCount: number;
  nightlyPrices: { date: string; price: number }[];
  totalAmount: number;
  agentId?: number;
  agentLinkId?: number;
  onBookingCreated: (bookingId: number) => void;
}

// Payment Form Component
function PaymentForm({ 
  clientSecret, 
  bookingId,
  onSuccess, 
  onError,
  setLocation
}: { 
  clientSecret: string;
  bookingId: number;
  onSuccess: (paymentIntentId: string) => void;
  onError: (error: Error | StripeError) => void;
  setLocation: (path: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [elementError, setElementError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    try {
      // First, confirm the payment
      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/bookings/${bookingId}/confirmation`,
        },
        redirect: 'if_required',
      });

      if (confirmError) {
        onError(confirmError);
        return;
      }

      // If we get here, the payment was successful
      // Get the payment intent from the elements
      const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);
      
      if (paymentIntent) {
        // Call onSuccess to update the booking status
        await onSuccess(paymentIntent.id);
        
        // Only redirect after the booking status is updated
        setLocation(`/bookings/${bookingId}/confirmation`);
      } else {
        throw new Error('Payment intent not found');
      }
    } catch (err) {
      onError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {elementError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
          <p className="text-red-800 text-sm">{elementError}</p>
        </div>
      )}
      <PaymentElement 
        onChange={(event) => {
          // PaymentElement doesn't have error property in onChange
          setElementError(null);
        }}
      />
      <Button type="submit" className="w-full mt-4" disabled={loading || !stripe || !elements}>
        {loading ? "Processing..." : "Pay Now"}
      </Button>
    </form>
  );
}

export default function BookingForm({
  propertyId,
  checkInDate,
  checkOutDate,
  guestCount,
  nightlyPrices,
  totalAmount,
  agentId,
  agentLinkId,
  onBookingCreated,
}: BookingFormProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [location, setLocation] = useLocation();
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [bookingId, setBookingId] = useState<number | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Calculate breakdown (in AED)
  // Assume nightlyPrices is an array of { date, price } where price is in AED
  const baseAmount = nightlyPrices.reduce((sum, n) => sum + n.price, 0);
  // For simplicity, assume markup and platform fee are included in totalAmount
  // If you have markup/platform fee breakdown, use those values
  const markupAmount = totalAmount - baseAmount > 0 ? totalAmount - baseAmount : 0;
  const platformFeeAmount = 0; // Set this if you have a platform fee breakdown

  // Redirect to login if not authenticated
  if (!user) {
    return (
      <div className="text-center py-8">
        <p className="mb-4">Please log in to complete your booking</p>
        <Button onClick={() => setLocation('/auth?tab=login')}>
          Log In
        </Button>
      </div>
    );
  }

  // Form
  const form = useForm<BookingFormValues>({
    resolver: zodResolver(bookingFormSchema),
    defaultValues: {
      guestName: user?.fullName || "",
      guestEmail: user?.email || "",
      guestPhone: user?.phone || "",
      specialRequests: "",
    },
    mode: "onChange", // Enable real-time validation
  });



  const onSubmit = async (data: BookingFormValues) => {
    try {
      setLoading(true);
      // Calculate totalAmountInFils
      const totalAmountInFils = Math.round(totalAmount * 100);
      if (totalAmountInFils < 200) {
        toast({
          title: "Invalid Amount",
          description: "The minimum payment amount must be at least 2 AED.",
          variant: "destructive",
        });
        return;
      }
      const guestName = user?.fullName || data.guestName;
      const guestEmail = user?.email || data.guestEmail;
      const guestPhone = user?.phone || data.guestPhone || null;
      // Create booking with nightlyPrices and totalAmount only
      const bookingResponse = await apiRequest<{ id: number }>(
        "POST",
        "bookings",
        {
          propertyId,
          checkIn: format(checkInDate, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
          checkOut: format(checkOutDate, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
          guestCount,
          nightlyPrices,
          totalAmount,
          totalAmountInFils,
          guestName,
          guestEmail,
          guestPhone,
          specialRequests: data.specialRequests || null,
          guestId: user?.id,
          agentId: agentId || null,
          agentLinkId: agentLinkId || null,
          status: BookingStatus.TEMPORARY,
        }
      );

      if (!bookingResponse?.id) {
        throw new Error("Failed to create booking");
      }

      setBookingId(bookingResponse.id);

      // Create payment intent with the booking ID using fils amount
      const paymentIntentResponse = await apiRequest<{ clientSecret: string }>(
        "POST", 
        "create-payment-intent", 
        {
          bookingId: bookingResponse.id,
          amount: totalAmountInFils,
          currency: "aed"
        }
      );

      if (!paymentIntentResponse.clientSecret) {
        throw new Error("No client secret received from server");
      }

      setClientSecret(paymentIntentResponse.clientSecret);
      
    } catch (error: any) {
      console.error("Error creating payment intent:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to initialize payment. Please try again.",
        variant: "destructive",
      });
      // Reset the form state
      setBookingId(null);
      setClientSecret(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntentId: string) => {
    if (!bookingId) {
      toast({
        title: "Error",
        description: "Booking ID not found. Please try again.",
        variant: "destructive",
      });
      return;
    }

    try {
      // First verify the booking exists
      const bookingResponse = await apiRequest(
        "GET",
        `bookings/${bookingId}`
      );

      if (!bookingResponse) {
        throw new Error("Booking not found");
      }

      // Update booking status to confirmed
      const updatedBooking = await apiRequest<{ id: number }>(
        "PUT", 
        `bookings/${bookingId}/status`, 
        {
          status: BookingStatus.CONFIRMED,
          stripePaymentIntentId: paymentIntentId
        }
      );

      onBookingCreated(updatedBooking.id);
      setPaymentSuccess(true); // Set success state
      return updatedBooking;
    } catch (error) {
      console.error("Error updating booking:", error);
      toast({
        title: "Error",
        description: "Payment successful but failed to update booking. Please contact support.",
        variant: "destructive",
      });
      throw error; // Re-throw to prevent redirect
    }
  };

  const handlePaymentError = (error: Error | StripeError) => {
    toast({
      title: "Payment Failed",
      description: error instanceof Error ? error.message : error.message || "Failed to process payment. Please try again.",
      variant: "destructive",
    });
  };

  // Guest menu items for sidebar
  const guestMenu = [
    { label: "My Bookings", href: "/bookings", icon: <Calendar /> },
    { label: "Profile", href: "/profile", icon: <User /> },
    { label: "Home", href: "/", icon: <Home /> },
    { label: "Logout", href: "/logout", icon: <LogOut /> },
  ];

  if (paymentSuccess) {
    return (
      <div className="flex">
        <Sidebar navItems={guestMenu} />
        <div className="flex-1 flex flex-col items-center justify-center py-16">
          <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full text-center">
            <h2 className="text-2xl font-bold mb-4 text-green-600">Booking Confirmed!</h2>
            <p className="mb-4">Your booking has been successfully completed. A confirmation email has been sent to you.</p>
            <Button onClick={() => setLocation('/bookings')}>View My Bookings</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!clientSecret ? (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Only show user details fields if user is not logged in */}
            {!user?.id && (
              <>
                <FormField
                  control={form.control}
                  name="guestName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter your full name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="guestEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input placeholder="your@email.com" type="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="guestPhone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number</FormLabel>
                      <FormControl>
                        <Input placeholder="+971 12 345 6789" type="tel" {...field} value={field.value || ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}
            {/* Always show special requests */}
            <FormField
              control={form.control}
              name="specialRequests"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Special Requests (optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Any special requests or requirements for your stay?"
                      rows={3}
                      {...field}
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <PaymentBreakdown 
              baseAmount={baseAmount}
              markupAmount={markupAmount}
              platformFeeAmount={platformFeeAmount}
              totalAmount={totalAmount}
              showDetails={user?.role === 'agent' || user?.role === 'manager'}
              userRole={user?.role}
            />
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
            >
              {loading ? "Processing..." : "Continue to Payment"}
            </Button>
          </form>
        </Form>
      ) : (
        <div className="space-y-4">
          <div className="bg-muted p-4 rounded-lg">
            <h3 className="font-semibold mb-2">Payment Details</h3>
            <p className="text-sm text-muted-foreground">
              Please complete your payment to confirm your booking.
            </p>
          </div>
          {clientSecret && clientSecret.startsWith('pi_') ? (
            <Elements 
              stripe={stripePromise} 
              options={{ 
                clientSecret,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#3b82f6',
                  },
                },
              }}
            >

              <PaymentForm 
                clientSecret={clientSecret}
                bookingId={bookingId!}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
              />
            </Elements>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-800 text-sm">
                Error: Invalid client secret format. Please try again.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}